import { Component } from "react";
import { RefreshCw, Home } from "lucide-react";
import KinglyLogo from "@/components/KinglyLogo";
import { reportError } from "@/lib/errorReporter";

/** Catches React render crashes anywhere in the app, reports them, and shows a recoverable screen. */
export default class AppErrorBoundary extends Component {
  state = { error: null };

  static getDerivedStateFromError(error) {
    return { error };
  }

  componentDidCatch(error, info) {
    // A chunk that 404s/timeouts after a deploy isn't a bug — the phone has a stale index. Reload once to pick up the new build.
    if (/Loading (CSS )?chunk .* failed|Failed to fetch dynamically imported module/i.test(String(error?.message || ""))) {
      const key = "kingly_chunk_reload";
      if (sessionStorage.getItem(key) !== window.location.href) {
        sessionStorage.setItem(key, window.location.href);
        window.location.reload();
        return;
      }
    }
    reportError("render", error, { component_stack: String(info?.componentStack || "").slice(0, 4000) });
  }

  render() {
    if (!this.state.error) return this.props.children;
    return (
      <div className="min-h-screen bg-[#0f1a2e] text-white flex items-center justify-center p-6" data-testid="app-crash-screen">
        <div className="max-w-md w-full text-center space-y-5">
          <div className="flex justify-center"><KinglyLogo size={56} /></div>
          <h1 className="font-display text-2xl font-bold">Well, that wasn't supposed to happen.</h1>
          <p className="text-sm text-slate-300 leading-relaxed">
            Something broke on this screen. We've logged exactly what happened so it gets fixed — your data is safe.
          </p>
          <p className="text-[11px] font-mono text-slate-500 break-words" data-testid="app-crash-message">
            {String(this.state.error?.message || this.state.error).slice(0, 200)}
          </p>
          <div className="flex gap-2 justify-center">
            <button onClick={() => this.setState({ error: null })} data-testid="app-crash-retry"
              className="inline-flex items-center gap-1.5 rounded-full bg-amber-400 text-[#0f1a2e] font-semibold px-5 py-2.5 text-sm hover:bg-amber-300">
              <RefreshCw className="w-4 h-4" /> Try again
            </button>
            <a href="/dashboard" data-testid="app-crash-home"
              className="inline-flex items-center gap-1.5 rounded-full border border-white/20 px-5 py-2.5 text-sm hover:bg-white/10">
              <Home className="w-4 h-4" /> Dashboard
            </a>
          </div>
        </div>
      </div>
    );
  }
}
